import { StyleSheet, TouchableOpacity, View, ViewProps } from 'react-native'
import { MesaMonogram } from '@/src/assets/icons/MesaMonogram'
import { type ComponentProps, type ComponentRef, forwardRef } from 'react'
import { classes, mesaColorNames } from '@/src/common/theme'

const headerStyles = StyleSheet.create({
  wrapper: {
    overflow: 'hidden',
    position: 'relative',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  monogram: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  },
  content: {
    position: 'relative',
    zIndex: 1,
  },
} as const)

type HeaderProps = Pick<ComponentProps<typeof View>, 'children'> & {
  containerStyle?: ViewProps['style']
  contentStyle?: ViewProps['style']
  disableMonogram?: boolean
}

function Header({ children, containerStyle, contentStyle, disableMonogram }: HeaderProps) {
  return (
    <View style={[headerStyles.wrapper, containerStyle]}>
      {!disableMonogram ? (
        <View style={headerStyles.monogram}>
          <MesaMonogram />
        </View>
      ) : null}
      <View style={[headerStyles.content, contentStyle]}>{children}</View>
    </View>
  )
}

type ImageHeaderProps = Pick<ComponentProps<typeof View>, 'children'> & {
  containerStyle?: ViewProps['style']
  contentStyle?: ViewProps['style']
}

function ImageHeader({ children, containerStyle, contentStyle }: ImageHeaderProps) {
  return (
    <View style={[headerStyles.wrapper, containerStyle]}>
      <View style={[headerStyles.monogram, contentStyle]}>{children}</View>
    </View>
  )
}

const contentStyles = StyleSheet.create({
  content: {
    padding: 16,
    paddingTop: 0,
  },
} as const)

type ContentProps = Pick<ComponentProps<typeof View>, 'children' | 'style'>

function Content({ children, style }: ContentProps) {
  return <View style={[contentStyles.content, style]}>{children}</View>
}

const footerStyles = StyleSheet.create({
  footer: {
    overflow: 'hidden',
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
  },
} as const)

type FooterProps = Pick<ComponentProps<typeof View>, 'children' | 'style'>

function Footer({ children, style }: FooterProps) {
  return <View style={[footerStyles.footer, style]}>{children}</View>
}

const splitCardStyles = StyleSheet.create({
  container: {
    backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'],
    padding: 0,
    borderRadius: 16,
    minHeight: 'auto',
    gap: 16,
    ...classes.cardShadow,
  },
} as const)

type SplitCardProps = Pick<
  ComponentProps<typeof TouchableOpacity>,
  'children' | 'onPress' | 'style' | 'testID'
>

export const SplitCard = Object.assign(
  forwardRef<ComponentRef<typeof TouchableOpacity>, SplitCardProps>(
    ({ children, onPress, style }, ref) => (
      <TouchableOpacity
        ref={ref}
        disabled={!onPress}
        onPress={onPress}
        style={[splitCardStyles.container, style]}
      >
        {children}
      </TouchableOpacity>
    ),
  ),
  { Header, Content, ImageHeader, Footer },
)
